import type { ReactNode } from "react";
import { config, isConfigured } from "../config";
import { shortAddr } from "../lib/format";
import { ensureChain } from "../lib/chain";
import { useWallet } from "../wallet";
import { Starfield } from "./Starfield";
import { Badge, Notice, Wordmark } from "./ui";

const NAV = [
  { href: "#/", label: "Home" },
  { href: "#/brains", label: "Brains" },
  { href: "#/inference", label: "Inference" },
  { href: "#/account", label: "Account" },
  { href: "#/operate", label: "Operate" },
  { href: "#/stake", label: "Stake" },
  { href: "#/govern", label: "Govern" },
  { href: "#/bridge", label: "Bridge" },
  { href: "#/api", label: "API" },
  { href: "#/docs", label: "Docs" },
  { href: "#/whitepaper", label: "Whitepaper" },
];

/** Active when the route is the nav path or one of its subsections (e.g. /docs/inference). */
function isActive(route: string, href: string): boolean {
  const path = href.slice(1);
  return route === path || (path !== "/" && route.startsWith(path + "/"));
}

export function Layout({ route, children }: { route: string; children: ReactNode }) {
  const { account, chainId, hasWallet, connect, connecting } = useWallet();
  const wrongChain = account !== undefined && chainId !== undefined && chainId !== config.l2.chainId;

  return (
    <>
      <div className="app-bg" />
      <Starfield />

      <header className="topbar">
        <a href="#/" style={{ textDecoration: "none" }}>
          <Wordmark />
        </a>
        <nav className="nav">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className={isActive(route, n.href) ? "active" : ""}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="spacer" />
        {account ? (
          <div className="row">
            <Badge kind={wrongChain ? "warn" : "ok"}>{wrongChain ? "Wrong network" : config.l2.name}</Badge>
            <span className="mono muted">{shortAddr(account)}</span>
          </div>
        ) : (
          <button className="btn sm" onClick={() => void connect()} disabled={!hasWallet || connecting}>
            {connecting ? "Connecting…" : hasWallet ? "Connect Wallet" : "No Wallet"}
          </button>
        )}
      </header>

      <main className="shell">
        {!isConfigured() && (
          <div style={{ marginTop: 18 }}>
            <Notice kind="warn">
              <strong>Preview mode.</strong> Contract addresses are not configured, so on-chain reads and writes
              will fail. Set them via <span className="mono">VITE_*</span> env vars (see{" "}
              <span className="mono">.env.example</span>) or <span className="mono">window.__BASEDAI_CONFIG__</span>{" "}
              once the network is deployed. The interface below is fully navigable meanwhile.
            </Notice>
          </div>
        )}
        {wrongChain && (
          <div style={{ marginTop: 18 }}>
            <Notice kind="warn">
              Your wallet is on chain {chainId}. This dApp runs on <strong>{config.l2.name}</strong> (chain{" "}
              {config.l2.chainId}).{" "}
              <a href="#" onClick={(e) => { e.preventDefault(); void ensureChain(config.l2); }}>
                Switch network
              </a>
              .
            </Notice>
          </div>
        )}
        {children}
        <footer className="footer">
          basedAI — decentralized LLM inference on Ink · fee-for-service, fixed-supply, no emissions ·{" "}
          <a href={config.l2.explorer} target="_blank" rel="noreferrer">
            explorer
          </a>
        </footer>
      </main>
    </>
  );
}
